import { useMemo, useState } from 'react';
import { useSpring, useTransition, animated } from '@react-spring/web';
import { ShieldCheck, Users, Mail, BarChart3, Building2 } from 'lucide-react';
import { useAuthStoreV1 } from '@/stores/authStore_v1';
import { useOrgMembers } from '@/hooks/org-admin';
import { RolesPanel } from './RolesPanel';
import { MembersPanel } from './MembersPanel';
import { InvitationsPanel } from './InvitationsPanel';
import { TokenUsagePanel } from './TokenUsagePanel';
import { OrgAdminOverview } from './OrgAdminOverview';
import type { OrgAdminTab, OrgAdminSelfContext } from '@/components/molecules/org-admin/types';

const TABS: OrgAdminTab[] = ['roles', 'members', 'invitations', 'usage'];
const TAB_META: Record<OrgAdminTab, { label: string; icon: typeof ShieldCheck }> = {
  roles: { label: 'Roles', icon: ShieldCheck },
  members: { label: 'Members', icon: Users },
  invitations: { label: 'Invitations', icon: Mail },
  usage: { label: 'Usage', icon: BarChart3 },
};

export function OrgAdminLayout() {
  const [activeTab, setActiveTab] = useState<OrgAdminTab>('roles');
  const tabIdx = TABS.indexOf(activeTab);

  const { data: members } = useOrgMembers();
  const authUserId = useAuthStoreV1((s) => s.user?.id ?? null);
  const orgName = useAuthStoreV1((s) => s.selectedOrg?.orgName ?? null);
  const self: OrgAdminSelfContext = useMemo(() => {
    const me = members?.find((m) => m.user_id === authUserId) ?? null;
    return { currentUserId: authUserId, currentUserRole: me?.management_level ?? null };
  }, [members, authUserId]);

  const underlineSpring = useSpring({
    left: `${((tabIdx + 0.5) / TABS.length) * 100}%`,
    config: { tension: 340, friction: 28 },
  });

  const contentTransition = useTransition(activeTab, {
    from: { opacity: 0, transform: 'translateY(4px)' },
    enter: { opacity: 1, transform: 'translateY(0px)' },
    leave: { opacity: 0, transform: 'translateY(0px)' },
    config: { tension: 260, friction: 32, clamp: true },
  });

  return (
    <div className="flex h-full flex-col overflow-hidden studio-card">
      {orgName && (
        <div className="flex items-center gap-2 px-3 pt-3 text-lg text-muted-foreground">
          <Building2 className="h-4 w-4" />
          Managing <span className="font-semibold text-foreground">{orgName}</span>
        </div>
      )}
      <OrgAdminOverview />
      <div className="relative flex shrink-0 border-b border-border">
        {TABS.map((tab) => {
          const { label, icon: Icon } = TAB_META[tab];
          return (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className={[
                'flex flex-1 items-center justify-center gap-1.5 py-2.5 text-sm transition-colors',
                activeTab === tab ? 'font-medium text-primary' : 'text-muted-foreground hover:text-foreground',
              ].join(' ')}
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
            </button>
          );
        })}
        <animated.div
          style={{ ...underlineSpring, transform: 'translateX(-50%)' }}
          className="absolute bottom-0 h-[2px] w-16 rounded-full bg-primary"
        />
      </div>

      <div className="relative flex-1 min-h-0">
        {contentTransition((style, tab) => (
          <animated.div style={style} className="absolute inset-0">
            {tab === 'roles' && <RolesPanel self={self} />}
            {tab === 'members' && <MembersPanel self={self} />}
            {tab === 'invitations' && <InvitationsPanel self={self} />}
            {tab === 'usage' && <TokenUsagePanel self={self} />}
          </animated.div>
        ))}
      </div>
    </div>
  );
}
